import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "./api";
import { useAuth } from "./AuthContext";
import ChangePasswordModal from "./ChangePasswordModal";
import RuleEditor from "./RuleEditor";
import { RULE_TYPES } from "./rule-constants";

export default function RulesPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [editing, setEditing] = useState(null);   // null = closed, {} = new, {id...} = edit
  const [showPwModal, setShowPwModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rules");
      setRows(data);
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    let r = rows;
    if (typeFilter !== "ALL") r = r.filter((x) => x.type === typeFilter);
    if (activeFilter !== "ALL") {
      const want = activeFilter === "ACTIVE";
      r = r.filter((x) => x.active === want);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter((x) =>
        [
          x.id, x.title, x.description, x.when_field, x.verdict,
          x.irregularity_site, x.policy_statement, x.match_field,
          x.applies_when_procedure_contains, x.expected_value,
          ...(x.when_alternatives || []),
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }
    return r;
  }, [rows, search, typeFilter, activeFilter]);

  const counts = useMemo(() => {
    const c = { DETECT: 0, ACCEPT_IF: 0, REJECT_IF: 0, GOLD_MATCH: 0 };
    rows.forEach((r) => (c[r.type] = (c[r.type] || 0) + 1));
    return c;
  }, [rows]);

  const onSaved = async () => {
    setEditing(null);
    await load();
  };

  const onDelete = async (rule) => {
    if (!window.confirm(`Delete rule ${rule.id}?\n\nThis is undoable via Version History.`)) return;
    try {
      await api.delete(`/rules/${rule.id}`);
      await load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const onToggleActive = async (rule) => {
    try {
      await api.put(`/rules/${rule.id}`, {
        ...rule,
        active: !rule.active,
      });
      await load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-rules-page" style={S.page}>
      <header style={S.header}>
        <div>
          <div style={S.brand}>Target Health · Master Rules</div>
          <div style={S.subbrand}>Single source of truth · auto-reloads Part B on save</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={S.userPill}>{user?.email}</span>
          <button data-testid="admin-changepw-btn"
                  onClick={() => setShowPwModal(true)}
                  style={S.linkBtn}>
            Change password
          </button>
          <button data-testid="admin-tester-btn"
                  onClick={() => nav("/admin/tester")}
                  style={{ ...S.linkBtn, borderColor: "#10b981", color: "#a7f3d0" }}>
            🧪 Test Claim
          </button>
          {user?.role === "admin" && (
            <button data-testid="admin-users-btn" onClick={() => nav("/admin/users")} style={S.linkBtn}>
              Users
            </button>
          )}
          <button data-testid="admin-logout-btn" onClick={logout} style={S.linkBtn}>
            Sign out
          </button>
        </div>
      </header>

      <section style={S.kpis}>
        <Kpi label="Total"      n={rows.length}              accent="#22d3ee" />
        <Kpi label="DETECT"     n={counts.DETECT || 0}        accent="#ef4444" />
        <Kpi label="ACCEPT IF"  n={counts.ACCEPT_IF || 0}    accent="#10b981" />
        <Kpi label="REJECT IF"  n={counts.REJECT_IF || 0}    accent="#f59e0b" />
        <Kpi label="GOLD MATCH" n={counts.GOLD_MATCH || 0}    accent="#8b5cf6" />
      </section>

      <section style={S.toolbar}>
        <input
          data-testid="admin-rules-search"
          placeholder="Search rules… (id, title, field, keyword)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={S.search}
        />
        <select
          data-testid="admin-rules-type-filter"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={S.select}
        >
          <option value="ALL">All types</option>
          {RULE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.value}
            </option>
          ))}
        </select>
        <select
          data-testid="admin-rules-active-filter"
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
          style={S.select}
        >
          <option value="ALL">Active + Inactive</option>
          <option value="ACTIVE">Active only</option>
          <option value="INACTIVE">Inactive only</option>
        </select>
        <button
          data-testid="admin-rules-new-btn"
          onClick={() => setEditing({})}
          style={S.primary}
        >
          + New rule
        </button>
      </section>

      <section style={S.tableWrap}>
        {loading ? (
          <div style={{ padding: 40, color: "#94a3b8" }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, color: "#94a3b8" }}>
            No rules match your filters.
          </div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>ID</th>
                <th style={S.th}>Type</th>
                <th style={S.th}>Active</th>
                <th style={S.th}>Title</th>
                <th style={S.th}>Trigger / Scope</th>
                <th style={S.th}>Outcome</th>
                <th style={S.th}>Updated</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`admin-rule-row-${r.id}`} style={S.tr}>
                  <td style={{ ...S.td, fontFamily: "monospace", color: "#cbd5e1" }}>
                    {r.id}
                  </td>
                  <td style={S.td}>
                    <TypeBadge type={r.type} />
                  </td>
                  <td style={S.td}>
                    <label style={{ cursor: "pointer" }}>
                      <input
                        data-testid={`admin-rule-active-${r.id}`}
                        type="checkbox"
                        checked={r.active}
                        onChange={() => onToggleActive(r)}
                        style={{ accentColor: "#10b981" }}
                      />
                    </label>
                  </td>
                  <td style={{ ...S.td, color: "#e2e8f0" }}>
                    <div>{r.title || <i style={{ color: "#64748b" }}>(no title)</i>}</div>
                  </td>
                  <td style={{ ...S.td, color: "#94a3b8", maxWidth: 280 }}>
                    {triggerSummary(r)}
                  </td>
                  <td style={{ ...S.td, color: "#94a3b8", maxWidth: 280 }}>
                    {outcomeSummary(r)}
                  </td>
                  <td style={{ ...S.td, color: "#64748b", fontSize: 12 }}>
                    {(r.updated_at || "").slice(0, 16).replace("T", " ")}
                    <div style={{ fontSize: 11 }}>{r.updated_by}</div>
                  </td>
                  <td style={S.td}>
                    <button
                      data-testid={`admin-rule-edit-${r.id}`}
                      onClick={() => setEditing(r)}
                      style={S.smallBtn}
                    >
                      Edit
                    </button>
                    <button
                      data-testid={`admin-rule-delete-${r.id}`}
                      onClick={() => onDelete(r)}
                      style={{ ...S.smallBtn, color: "#fca5a5" }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {editing !== null && (
        <RuleEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}

      <ChangePasswordModal
        open={showPwModal}
        onClose={() => setShowPwModal(false)}
      />
    </div>
  );
}

function Kpi({ label, n, accent }) {
  return (
    <div style={{ ...S.kpi, borderTop: `3px solid ${accent}` }}>
      <div style={S.kpiN}>{n}</div>
      <div style={S.kpiL}>{label}</div>
    </div>
  );
}

function TypeBadge({ type }) {
  const map = {
    DETECT:     { bg: "#7f1d1d", fg: "#fecaca" },
    ACCEPT_IF:  { bg: "#064e3b", fg: "#a7f3d0" },
    REJECT_IF:  { bg: "#78350f", fg: "#fde68a" },
    GOLD_MATCH: { bg: "#4c1d95", fg: "#ddd6fe" },
  };
  const c = map[type] || { bg: "#334155", fg: "#cbd5e1" };
  return (
    <span style={{
      background: c.bg, color: c.fg, padding: "3px 8px", borderRadius: 4,
      fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
    }}>
      {type.replace("_", " ")}
    </span>
  );
}

function triggerSummary(r) {
  if (r.type === "DETECT") {
    const alts = (r.when_alternatives || []).filter(Boolean);
    if (!r.when_field && !alts.length) return <i>—</i>;
    return (
      <span title={alts.join(" | ")}>
        <b style={{ color: "#cbd5e1" }}>{r.when_field}</b>
        {" matches "}
        <code style={{ fontSize: 12, color: "#fbbf24" }}>
          {alts.length > 1
            ? `${alts[0]} ${"…"}+${alts.length - 1}`
            : alts[0] || "?"}
        </code>
      </span>
    );
  }
  if (r.type === "ACCEPT_IF" || r.type === "REJECT_IF") {
    return r.applies_when_procedure_contains
      ? <span>procedure contains <b style={{ color: "#cbd5e1" }}>{r.applies_when_procedure_contains}</b></span>
      : <i>all procedures</i>;
  }
  if (r.type === "GOLD_MATCH") {
    return (
      <span>
        {r.applies_when_procedure_contains ? <>procedure: <b>{r.applies_when_procedure_contains}</b><br /></> : null}
        field <b style={{ color: "#cbd5e1" }}>{r.match_field}</b>{" "}
        ({r.match_mode})
      </span>
    );
  }
  return <i>—</i>;
}

function outcomeSummary(r) {
  if (r.type === "DETECT") {
    return (
      <span>
        <b style={{ color: verdictColor(r.verdict) }}>{r.verdict || "?"}</b>
        {r.irregularity_site && <> · {r.irregularity_site}</>}
      </span>
    );
  }
  if (r.type === "ACCEPT_IF" || r.type === "REJECT_IF") {
    return r.policy_statement
      ? <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block", maxWidth: 280 }}>{r.policy_statement.slice(0, 80)}…</span>
      : <i>—</i>;
  }
  if (r.type === "GOLD_MATCH") {
    return (
      <span>
        if mismatch → <b style={{ color: verdictColor(r.severity_if_mismatch) }}>
          {r.severity_if_mismatch}
        </b>
      </span>
    );
  }
  return <i>—</i>;
}

function verdictColor(v) {
  if (v === "Irregularity +ve") return "#fca5a5";
  if (v === "CLARIFY") return "#fde68a";
  if (v === "NO Irregularity") return "#a7f3d0";
  return "#cbd5e1";
}

const S = {
  page: {
    minHeight: "100vh",
    background: "#0b1220",
    color: "#e2e8f0",
    fontFamily: "ui-sans-serif,system-ui,-apple-system,'Segoe UI',sans-serif",
    padding: "20px 24px 48px",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    marginBottom: 16,
  },
  brand: { fontSize: 18, fontWeight: 700, color: "#f1f5f9" },
  subbrand: { fontSize: 12, color: "#64748b", marginTop: 2 },
  userPill: {
    background: "#0f172a", border: "1px solid #1f2a44", padding: "4px 10px",
    borderRadius: 20, fontSize: 12, color: "#94a3b8",
  },
  linkBtn: {
    background: "transparent", border: "1px solid #334155", color: "#cbd5e1",
    padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12,
  },
  kpis: { display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 20 },
  kpi: {
    background: "#0f172a", border: "1px solid #1f2a44", borderRadius: 10,
    padding: "12px 16px",
  },
  kpiN: { fontSize: 24, fontWeight: 700, color: "#f1f5f9" },
  kpiL: { fontSize: 11, color: "#94a3b8", letterSpacing: 1, textTransform: "uppercase", marginTop: 2 },
  toolbar: { display: "flex", gap: 8, marginBottom: 12, alignItems: "center" },
  search: {
    flex: 1, padding: "10px 14px", borderRadius: 8, border: "1px solid #334155",
    background: "#0f172a", color: "#e2e8f0", fontSize: 14, outline: "none",
  },
  select: {
    padding: "10px 12px", borderRadius: 8, border: "1px solid #334155",
    background: "#0f172a", color: "#e2e8f0", fontSize: 13, cursor: "pointer",
  },
  primary: {
    padding: "10px 16px", borderRadius: 8, border: "none",
    background: "#10b981", color: "white", fontWeight: 700, cursor: "pointer",
  },
  tableWrap: {
    background: "#0f172a", border: "1px solid #1f2a44", borderRadius: 10,
    overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left", padding: "10px 12px", color: "#64748b",
    fontSize: 11, letterSpacing: 1, textTransform: "uppercase",
    background: "#0b1220", borderBottom: "1px solid #1f2a44",
  },
  tr: { borderBottom: "1px solid #131c2f" },
  td: { padding: "12px 12px", verticalAlign: "top" },
  smallBtn: {
    background: "transparent", border: "1px solid #334155", color: "#cbd5e1",
    padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12,
    marginRight: 6,
  },
};
